/**
 * Handling horizontal or vertical overscroll events which execute function past in as props
 * it also animates the parent component according to the setting
 * 
 * Importants!!!
 * SwipeOverscrollHandler must be positioned at root components to prevent listeners being readd on child components rerendered
 */
import { Fragment, useState, useRef, useEffect } from 'react';

const nonstate = {
    defaultDirection: 'top-to-bottom',
    defaultTouchTreshold: 80
}

interface Props {
    targetRef: any,
    direction?: string, //right-to-left, top-to-bottom, bottom-to-top, left-to-right
    action?: any,
    touchTreshold?: number,
    animation?: boolean
}

const SwipeOverscrollHandler: React.FC<Props> = ({ targetRef, direction, action, touchTreshold, animation }) => {
    //refs
    const currentScrollPosRef = useRef<number>(0);
    const initialViewTouchPosRef = useRef<number>(0);
    const finalViewTouchPosRef = useRef<number>(0);
    const watchForTargetIntervalRef = useRef<any>(null);

    // const galleryPagePublicRef = useRef<any>(null);
    // const galleryPageMyPostRef = useRef<any>(null);
    const [target, SetTarget] = useState<any>(targetRef.current)
    const [refreshBounce, SetRefreshBounce] = useState('rest'); //rest - drag(pulled down)

    //initialize
    useEffect(() => {
        //wait for the target to mount, after mount stop the interval
        watchForTargetIntervalRef.current = setInterval(() => {
            if (targetRef.current != undefined && targetRef.current != null) {
                SetTarget(targetRef.current);
                clearInterval(watchForTargetIntervalRef.current);
            }
        }, 500);

        return (() => {
            if (watchForTargetIntervalRef.current != null) {
                clearInterval(watchForTargetIntervalRef.current);
            }
        });
    }, []);

    useEffect(() => {
        if (
            target != null
            && targetRef.current != null
            && targetRef.current != undefined
        ) {
            //remove scroll event handlers
            targetRef.current.removeEventListener('touchstart', OnTouchStart);
            targetRef.current.removeEventListener('touchmove', OnTouchMove);
            targetRef.current.removeEventListener('touchend', OnTouchEnd);
            //add scroll event handlers
            targetRef.current.addEventListener('touchstart', OnTouchStart);
            targetRef.current.addEventListener('touchmove', OnTouchMove);
            targetRef.current.addEventListener('touchend', OnTouchEnd);
            //add custom style
            targetRef.current.style.transition = 'transform .25s';
            targetRef.current.style.transform = 'translate(0px, 0px)';
        }
    }, [target]);

    useEffect(() => {
        if (
            targetRef.current != null
            && targetRef.current != undefined
            && (animation == undefined || animation)
        ) {
            switch (refreshBounce) {
                case 'rest':
                    targetRef.current.style.transform = 'translate(0px, 0px)';
                    break;
                case 'drag':
                    let _direction = (direction != undefined ? direction : nonstate.defaultDirection);
                    switch (_direction) {
                        case 'top-to-bottom':
                            targetRef.current.style.transform = 'translate(0, 3vh)';
                            break;
                        case 'bottom-to-top':
                            targetRef.current.style.transform = 'translate(0px, -3vh)';
                            break;
                        case 'left-to-right':
                            targetRef.current.style.transform = 'translate(3vh, 0px)';
                            break;
                        case 'right-to-left':
                            targetRef.current.style.transform = 'translate(-3vh, 0)';
                            break;
                    }
                    break;
            }
        }
    }, [refreshBounce]);

    //test
    // useEffect(() => {
    //     console.log('SwipeOverscrollHandlerTest: ' + refreshBounce);
    // }, [refreshBounce]);

    //ui functions
    const OnTouchStart = (te: React.TouchEvent) => {
        if (
            targetRef.current != null
            && targetRef.current != undefined
        ) {
            let _direction = (direction != undefined ? direction : nonstate.defaultDirection);
            switch (_direction) {
                case 'top-to-bottom':
                case 'bottom-to-top':
                    currentScrollPosRef.current = targetRef.current.scrollTop;
                    initialViewTouchPosRef.current = te.touches[0].clientY;
                    break;
                case 'left-to-right':
                case 'right-to-left':
                    currentScrollPosRef.current = targetRef.current.scrollLeft;
                    initialViewTouchPosRef.current = te.touches[0].clientX;
                    break;
            }
        }
    }
    const OnTouchMove = (te: React.TouchEvent) => {
        if (
            targetRef.current != null
            && targetRef.current != undefined
        ) {
            let dir = (direction != undefined ? direction : nonstate.defaultDirection);
            switch (dir) {
                case 'top-to-bottom':
                    finalViewTouchPosRef.current = te.touches[0].clientY;
                    if (currentScrollPosRef.current == 0 && (finalViewTouchPosRef.current - initialViewTouchPosRef.current >= (touchTreshold != undefined ? touchTreshold : nonstate.defaultTouchTreshold))) {
                        SetRefreshBounce('drag');
                    }
                    break;
                case 'bottom-to-top':
                    finalViewTouchPosRef.current = te.touches[0].clientY;
                    if (currentScrollPosRef.current == 0 && (initialViewTouchPosRef.current - finalViewTouchPosRef.current >= (touchTreshold != undefined ? touchTreshold : nonstate.defaultTouchTreshold))) {
                        SetRefreshBounce('drag');
                    }
                    break;
                case 'left-to-right':
                    finalViewTouchPosRef.current = te.touches[0].clientX;
                    if (currentScrollPosRef.current == 0 && (finalViewTouchPosRef.current - initialViewTouchPosRef.current >= (touchTreshold != undefined ? touchTreshold : nonstate.defaultTouchTreshold))) {
                        SetRefreshBounce('drag');
                    }
                    break;
                case 'right-to-left':
                    finalViewTouchPosRef.current = te.touches[0].clientX;
                    if (currentScrollPosRef.current == 0 && (initialViewTouchPosRef.current - finalViewTouchPosRef.current >= (touchTreshold != undefined ? touchTreshold : nonstate.defaultTouchTreshold))) {
                        SetRefreshBounce('drag');
                    }
                    break;
            }
        }
    }
    const OnTouchEnd = (te: React.TouchEvent) => {
        if(
            targetRef.current != null
            && targetRef.current != undefined
        ) {
            let dir = (direction != undefined ? direction : nonstate.defaultDirection);
            switch (dir) {
                case 'top-to-bottom':
                case 'left-to-right':
                    if (currentScrollPosRef.current == 0 && (finalViewTouchPosRef.current - initialViewTouchPosRef.current >= (touchTreshold != undefined ? touchTreshold : nonstate.defaultTouchTreshold))) {
                        if (action != undefined) {
                            action();
                        }
                        initialViewTouchPosRef.current = 0;
                        finalViewTouchPosRef.current = 0;
                        SetRefreshBounce('rest');
                    }
                    break;
                case 'bottom-to-top':
                case 'right-to-left':
                    if (currentScrollPosRef.current == 0 && (initialViewTouchPosRef.current - finalViewTouchPosRef.current >= (touchTreshold != undefined ? touchTreshold : nonstate.defaultTouchTreshold))) {
                        if (action != undefined) {
                            action();
                        }
                        initialViewTouchPosRef.current = 0;
                        finalViewTouchPosRef.current = 0;
                        SetRefreshBounce('rest');
                    }
                    break;
            }
        }
    }

    return (
        <Fragment></Fragment>
    );
}
export default SwipeOverscrollHandler;